import {HttpClient} from '@angular/common/http';
import {computed, inject, Injectable, signal} from '@angular/core';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {NavigationEnd, Router} from '@angular/router';
import {ChangePassword} from '@domain/password/password';
import {Status} from '@domain/response-status';
import {ClinicianService} from '@infrastructure/clinician/clinician.service';
import {RequestStatus} from '@infrastructure/request-status.enum';
import {TranslateService} from '@ngx-translate/core';
import {catchError, EMPTY, filter, map, merge, Observable, Subject, switchMap, tap} from 'rxjs';
import {TokenResponse} from 'src/domain/token-response';
import {SnackBarService} from 'src/shared/snackbar/snackbar.service';
import {LocalStorageKeys} from 'src/shared/utils/local-storage-keys.enum';
import {AdminService} from '../admin/admin.service';
import {OwnerService} from '../owner/owner.service';
import {UserType} from './user-type.enum';

@Injectable({
    providedIn: 'root'
})
export class GenericAuthService
{
    private readonly httpClient = inject(HttpClient);

    private readonly ownerService = inject(OwnerService);

    private readonly adminService = inject(AdminService);

    private readonly clinicianService = inject(ClinicianService);

    private readonly translate = inject(TranslateService);

    private readonly snackBar = inject(SnackBarService);

    private readonly router = inject(Router);

    private readonly emailState = signal<Status>({
        status: RequestStatus.NOT_EXECUTED
    });

    private readonly state = signal<Status>({
        status: RequestStatus.NOT_EXECUTED
    });

    private readonly passwordState = signal<Status>({
        status: RequestStatus.NOT_EXECUTED
    });

    public readonly passwordLoading = computed(() => this.passwordState().status === RequestStatus.PENDING);

    public readonly loadingEmailRequest = computed(() => this.emailState().status === RequestStatus.PENDING);

    public readonly showSuccess = signal<boolean>(false);

    public readonly isOwner = toSignal(this.router.events.pipe(filter((event) => event instanceof NavigationEnd), map(() => this.router.url.includes(UserType.OWNER) && !!localStorage.getItem(LocalStorageKeys.OWNER_ACCESS_TOKEN))));

    public readonly isAdmin = toSignal(this.router.events.pipe(filter((event) => event instanceof NavigationEnd), map(() => this.router.url.includes(UserType.ADMIN) && !!localStorage.getItem(LocalStorageKeys.ADMIN_ACCESS_TOKEN))));

    public readonly isClinician = toSignal(this.router.events.pipe(filter((event) => event instanceof NavigationEnd), map(() => this.router.url.includes(UserType.CLINICIAN) && !!localStorage.getItem(LocalStorageKeys.CLINICIAN_ACCESS_TOKEN))));

    public readonly loggedUserType = computed(() =>
    {
        if (this.isOwner() || this.getUserTypeFromApiRequest(window.location.href, '') === UserType.OWNER)
        {
            return UserType.OWNER;
        }
        else if (this.isAdmin() || this.getUserTypeFromApiRequest(window.location.href, '') === UserType.ADMIN)
        {
            return UserType.ADMIN;
        }
        else if (this.isClinician() || this.getUserTypeFromApiRequest(window.location.href, '') === UserType.CLINICIAN)
        {
            return UserType.CLINICIAN;
        }
    });

    public isAuthenticated = computed(() => this.isOwner() || this.isClinician() || this.isAdmin());

    public readonly userType = computed(() => this.loggedUserType());

    private readonly logout$ = new Subject();

    private readonly loggedOut$ = this.logout$.pipe(
        switchMap(() =>
        {
            return this.httpClient.delete(`${this.loggedUserType()}/logout`, {body: {refreshToken: this.getRefreshToken(this.loggedUserType())}}).pipe(
                catchError(() =>
                {
                    this.snackBar.showErrorSnackbar(this.translate.instant('PROFILE.ERROR_MESSAGE'));

                    return EMPTY;
                }));
        })
    );

    private readonly sendRecoverPasswordEmail$ = new Subject<{email: string; userType: UserType}>();

    private readonly sendingRecoverPasswordEmail$ = this.sendRecoverPasswordEmail$.pipe(
        switchMap(({email, userType}) =>
        {
            return this.httpClient.post<unknown>(`${userType}/recover`, {email}).pipe(
                catchError(() =>
                {
                    this.snackBar.showErrorSnackbar(this.translate.instant('GENERAL.RESPONSE_ERRORS.EMAIL_SEND_NOT_COMPLETE'));

                    this.emailState.set({
                        status: RequestStatus.ERROR
                    });
    
                    return EMPTY;
                })
            );
        })
    );
    
    private readonly recoverPassword$ = new Subject<{token: string; password: string; userType: UserType}>();

    private readonly recoveringPassword$ = this.recoverPassword$.pipe(
        switchMap(({token, password, userType}) =>
        {
            return this.httpClient.post<unknown>(`${userType}/new-password`, {password, token}).pipe(
                catchError(() =>
                {
                    this.snackBar.showErrorSnackbar(this.translate.instant('GENERAL.RESPONSE_ERRORS.PASSWORD_CHANGE_NOT_COMPLETE'));

                    this.state.set({
                        status: RequestStatus.ERROR
                    });
    
                    return EMPTY;
                })
            );
        })
    );

    private readonly updatePassword$ = new Subject<ChangePassword>();

    private readonly passwordUpdated$ = this.updatePassword$.pipe(
        switchMap((changePassword) =>
        {
            return this.httpClient.patch(`${this.loggedUserType()}/password`, changePassword).pipe(catchError(() =>
            {
                this.passwordState.set({
                    status: RequestStatus.ERROR
                });
                
                this.snackBar.showErrorSnackbar(this.translate.instant('PROFILE.ERROR_MESSAGE_CHANGE_PASSWORD'));

                return EMPTY;
            }));
        }),
        tap(() => this.snackBar.showSuccessSnackbar(this.translate.instant('PROFILE.SUCCESS_MESSAGE_CHANGE_PASSWORD')))
    );

    public constructor()
    {
        this.loggedOut$.pipe(takeUntilDestroyed())
            .subscribe({
                next: () =>
                {
                    this.clearLoggedUserAndNavigateToLogin();
                }
            });

        merge(this.sendingRecoverPasswordEmail$, this.recoveringPassword$).pipe(takeUntilDestroyed()).subscribe({
            next: () =>
            {
                this.showSuccess.set(true);

                this.emailState.set({
                    status: RequestStatus.CONCLUDED
                });
            }
        });

        this.passwordUpdated$
            .pipe(takeUntilDestroyed())
            .subscribe({
                next: () =>
                {
                    this.clearLoggedUserAndNavigateToLogin();

                    this.passwordState.set({
                        status: RequestStatus.CONCLUDED
                    });
                    
                    this.router.navigateByUrl(`${this.loggedUserType()}/login`);
                }
            });
    }

    private clearLoggedUserAndNavigateToLogin(): void
    {
        this.clearTokens(this.loggedUserType());
        this.ownerService.clearOwner();
        this.adminService.clearAdmin();
        this.clinicianService.clearClinician();

        this.router.navigateByUrl(`${this.loggedUserType()}/login`);
    }

    public refreshAccessToken(userType: UserType): Observable<{accessToken: string; refreshToken: string}>
    {
        return this.httpClient.post(`${userType}/refresh-token`, {refreshToken: this.getRefreshToken(userType)}).pipe(
            map((responseJSON: TokenResponse) => ({
                accessToken: responseJSON.accessToken,
                refreshToken: responseJSON.refreshToken
            })),
            tap(({accessToken, refreshToken: newRefreshToken}) => this.saveTokens(accessToken, newRefreshToken, userType)),
            catchError(() =>
            {
                this.clearLoggedUserAndNavigateToLogin();

                return EMPTY;
            })
        );
    }

    public saveTokens(accessToken: string, refreshToken: string, userType: UserType): void
    {
        switch (userType)
        {
            case UserType.ADMIN:
                localStorage.setItem(LocalStorageKeys.ADMIN_ACCESS_TOKEN, accessToken);
                localStorage.setItem(LocalStorageKeys.ADMIN_REFRESH_TOKEN, refreshToken);
                break;
            case UserType.CLINICIAN:
                localStorage.setItem(LocalStorageKeys.CLINICIAN_ACCESS_TOKEN, accessToken);
                localStorage.setItem(LocalStorageKeys.CLINICIAN_REFRESH_TOKEN, refreshToken);
                break;
            case UserType.OWNER:
                localStorage.setItem(LocalStorageKeys.OWNER_ACCESS_TOKEN, accessToken);
                localStorage.setItem(LocalStorageKeys.OWNER_REFRESH_TOKEN, refreshToken);
            default:
                break;
        }
    }

    public clearTokens(userType: UserType): void
    {
        switch (userType)
        {
            case UserType.ADMIN:
                localStorage.removeItem(LocalStorageKeys.ADMIN_ACCESS_TOKEN);
                localStorage.removeItem(LocalStorageKeys.ADMIN_REFRESH_TOKEN);
                break;
            case UserType.CLINICIAN:
                localStorage.removeItem(LocalStorageKeys.CLINICIAN_ACCESS_TOKEN);
                localStorage.removeItem(LocalStorageKeys.CLINICIAN_REFRESH_TOKEN);
                break;
            case UserType.OWNER:
                localStorage.removeItem(LocalStorageKeys.OWNER_ACCESS_TOKEN);
                localStorage.removeItem(LocalStorageKeys.OWNER_REFRESH_TOKEN);
            default:
                break;
        }
    }

    public logout(): void
    {
        this.logout$.next({});
    }

    public sendRecoverPasswordEmail(email: string, userType: UserType): void
    {
        this.showSuccess.set(false);
        this.emailState.set({
            status: RequestStatus.PENDING
        });
        this.sendRecoverPasswordEmail$.next({email, userType});
    }

    public clearSuccessLabel(): void
    {
        this.showSuccess.set(false);
    }

    public recoverPassword(token: string, password: string, userType: UserType): void
    {
        this.showSuccess.set(false);
        this.emailState.set({
            status: RequestStatus.PENDING
        });
        this.recoverPassword$.next({token, password, userType});
    }

    public updatePassword(changePassword: ChangePassword): void
    {
        this.passwordState.set({
            status: RequestStatus.PENDING
        });

        this.updatePassword$.next(changePassword);
    }

    public toggleProfileAndFetchInformation(): void
    {
        if (this.isAdmin() || this.isClinician())
        {
            const storedAccessToken = localStorage.getItem(this.isAdmin() ?
                LocalStorageKeys.CLINICIAN_ACCESS_TOKEN :
                LocalStorageKeys.ADMIN_ACCESS_TOKEN
            );
            const storedRefreshToken = localStorage.getItem(this.isAdmin() ?
                LocalStorageKeys.CLINICIAN_REFRESH_TOKEN :
                LocalStorageKeys.ADMIN_REFRESH_TOKEN
            );

            if (!!storedAccessToken && !!storedRefreshToken)
            {
                this.router.navigateByUrl(`dashboard/${this.isAdmin() ? 'clinician' : 'admin'}/profile`);
            }
            else
            {
                this.router.navigateByUrl(`${this.isAdmin() ? 'clinician' : 'admin'}/login`);
            }
        }
    }

    public hasAnyTokenStoredForUser(url: string): boolean
    {
        return url.includes(UserType.OWNER) && !!localStorage.getItem(LocalStorageKeys.OWNER_ACCESS_TOKEN) && !!localStorage.getItem(LocalStorageKeys.OWNER_REFRESH_TOKEN) ||
            url.includes(UserType.ADMIN) && !!localStorage.getItem(LocalStorageKeys.ADMIN_ACCESS_TOKEN) && !!localStorage.getItem(LocalStorageKeys.ADMIN_REFRESH_TOKEN) ||
            url.includes(UserType.CLINICIAN) && !!localStorage.getItem(LocalStorageKeys.CLINICIAN_ACCESS_TOKEN) && !!localStorage.getItem(LocalStorageKeys.CLINICIAN_REFRESH_TOKEN);
    }

    public getUserTypeFromApiRequest(clientUrl: string, apiUrl: string): UserType
    {
        let userType: UserType;

        if (apiUrl.startsWith(UserType.OWNER) || clientUrl.includes(UserType.OWNER))
        {
            userType = UserType.OWNER;
        }
        else if (apiUrl.startsWith(UserType.ADMIN) || clientUrl.includes(UserType.ADMIN))
        {
            userType = UserType.ADMIN;
        }
        else if (apiUrl.startsWith(UserType.CLINICIAN) || clientUrl.includes(UserType.CLINICIAN))
        {
            userType = UserType.CLINICIAN;
        }

        return userType;
    }

    public getAccessToken(userType: UserType): string
    {
        let accessToken;

        if (userType === UserType.OWNER)
        {
            accessToken = localStorage.getItem(LocalStorageKeys.OWNER_ACCESS_TOKEN);
        }
        else if (userType === UserType.ADMIN)
        {
            accessToken = localStorage.getItem(LocalStorageKeys.ADMIN_ACCESS_TOKEN);
        }
        else if (userType === UserType.CLINICIAN)
        {
            accessToken = localStorage.getItem(LocalStorageKeys.CLINICIAN_ACCESS_TOKEN);
        }
    
        return accessToken;
    }

    public getRefreshToken(userType: UserType): string
    {
        let refreshToken;

        if (userType === UserType.OWNER)
        {
            refreshToken = localStorage.getItem(LocalStorageKeys.OWNER_REFRESH_TOKEN);
        }
        else if (userType === UserType.ADMIN)
        {
            refreshToken = localStorage.getItem(LocalStorageKeys.ADMIN_REFRESH_TOKEN);
        }
        else if (userType === UserType.CLINICIAN)
        {
            refreshToken = localStorage.getItem(LocalStorageKeys.CLINICIAN_REFRESH_TOKEN);
        }
    
        return refreshToken;
    }
}
